import React, { useState, useEffect, useRef } from 'react';
import { content, colors } from '../data/content';

const Counter = ({ language }) => {
  const currentContent = content[language].counter;
  const isRTL = language === 'ar';
  const [isVisible, setIsVisible] = useState(false);
  const [mainCount, setMainCount] = useState(0);
  const [statsCounts, setStatsCounts] = useState({});
  const sectionRef = useRef(null);
  const hasAnimated = useRef(false);

  const extractNumber = (str) => {
    const match = str.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const getSuffix = (str) => {
    if (str.includes('+')) return '+';
    if (str.includes('%')) return '%';
    return '';
  };

  const animateCounter = (target, duration, callback) => {
    const start = 0;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(start + (target - start) * easeOut);
      
      callback(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        callback(target);
      }
    };

    requestAnimationFrame(animate);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            setIsVisible(true);
            hasAnimated.current = true;

            const mainTarget = extractNumber(currentContent.mainNumber);
            animateCounter(mainTarget, 2000, (value) => {
              setMainCount(value);
            });

            currentContent.stats.forEach((stat, index) => {
              const target = extractNumber(stat.number);
              const suffix = getSuffix(stat.number);
              
              setTimeout(() => {
                animateCounter(target, 2000, (value) => {
                  setStatsCounts(prev => ({
                    ...prev,
                    [index]: { value, suffix }
                  }));
                });
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [currentContent]);

  return (
    <section
      id="counter"
      ref={sectionRef}
      style={{
        background: `linear-gradient(135deg, ${colors.secondary} 0%, #1a2d5e 50%, ${colors.primary} 100%)`,
        backgroundSize: '200% 200%',
        animation: 'gradient 8s ease infinite',
        padding: 'clamp(60px, 10vw, 100px) clamp(20px, 4vw, 40px)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden'
      }}>
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: `${Math.random() * 100 + 50}px`,
              height: `${Math.random() * 100 + 50}px`,
              background: `rgba(255, 255, 255, ${Math.random() * 0.1 + 0.05})`,
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              filter: 'blur(20px)',
              animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 clamp(20px, 4vw, 40px)',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Main Counter */}
        <div
          style={{
            fontSize: 'clamp(3.5rem, 10vw, 6rem)',
            fontWeight: 900,
            color: colors.accent,
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            background: `linear-gradient(135deg, ${colors.accent} 0%, #fff 50%, ${colors.accent} 100%)`,
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: isVisible ? 'shimmer 3s linear infinite' : 'none',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'scale(1)' : 'scale(0.5)',
            transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
            textShadow: isVisible ? '0 0 40px rgba(253, 236, 60, 0.5)' : 'none',
            marginBottom: 'clamp(16px, 3vw, 24px)'
          }}
        >
          {mainCount}{getSuffix(currentContent.mainNumber)}
        </div>
        
        <p style={{
          color: 'white',
          fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
          marginTop: 'clamp(12px, 3vw, 20px)',
          fontWeight: 600,
          fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.8s ease 0.3s'
        }}>
          {currentContent.mainText}
        </p>
        
        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'clamp(24px, 5vw, 40px)',
          marginTop: 'clamp(48px, 8vw, 64px)',
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {currentContent.stats.map((stat, index) => {
            const countData = statsCounts[index];
            const displayValue = countData 
              ? `${countData.value}${countData.suffix}` 
              : '0';
            const isStatVisible = countData !== undefined;
            
            return (
              <div 
                key={index} 
                style={{ 
                  textAlign: 'center',
                  opacity: isStatVisible ? 1 : 0,
                  transform: isStatVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ease ${index * 0.2}s`
                }}
              >
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: 'clamp(24px, 4vw, 32px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{
                    fontSize: 'clamp(2rem, 5vw, 3rem)',
                    fontWeight: 800,
                    color: colors.accent,
                    fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                    marginBottom: '8px',
                    textShadow: '0 0 20px rgba(253, 236, 60, 0.5)'
                  }}>
                    {displayValue}
                  </div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: 'clamp(0.9375rem, 2vw, 1.125rem)',
                    fontWeight: 500,
                    fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
                  }}>
                    {stat.label}
                  </div>
                  
                  {/* Decorative Glow */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100px',
                    height: '100px',
                    background: `radial-gradient(circle, ${colors.accent}20 0%, transparent 70%)`,
                    borderRadius: '50%',
                    filter: 'blur(20px)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    pointerEvents: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0';
                  }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Counter;
